import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EditButton,
    ChipField,
    Filter,
    TextInput,
    SelectInput,
    ReferenceField,
    ReferenceInput,
    AutocompleteInput,
    FunctionField,
    Link,
    DateField,
    ShowButton,
} from 'react-admin';
import { Chip } from '@material-ui/core';

import UserReferenceField from '../components/UserReferenceField';
import BoxitChipField from '../components/BoxitChipField';
import CustomSearchInput from '../components/CustomSearchInput';
import { BoxStatus } from '../constants';
import CountryField from '../components/CountryField';
import { SelectArrayInput } from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';

// Custom field components for nested objects
const CustomerNameField = ({ record }) => {
    if (record && record.user && record.user.name) {
        return (
            <Link to={`/users/${record.user.id}/show`}>{record.user.name}</Link>
        );
    } else if (record && record.userId) {
        return <UserReferenceField record={record} label="Customer" />;
    }
    return null;
};

const PartnerNameField = ({ record }) => {
    if (record && record.partner && record.partner.name) {
        return (
            <Link to={`/partners/${record.partner.id}/show`}>
                {record.partner.name}
            </Link>
        );
    } else if (record && record.partnerId) {
        return (
            <ReferenceField
                record={record}
                source="partnerId"
                reference="partners"
                link="show"
            >
                <TextField source="name" />
            </ReferenceField>
        );
    }
    return null;
};

const CountryNameField = ({ record }) => {
    if (record && record.country && record.country.name) {
        return (
            <Link to={`/country/${record.country.id}/show`}>
                {record.country.name}
            </Link>
        );
    } else if (record && record.countryId) {
        return <CountryField record={record} />;
    }
    return null;
};

const WarehouseNameField = ({ record }) => {
    if (record && record.warehouse && record.warehouse.name) {
        return (
            <Link to={`/warehouses/${record.warehouse.id}/show`}>
                {record.warehouse.name}
            </Link>
        );
    } else if (record && record.warehouseId) {
        return (
            <ReferenceField
                record={record}
                source="warehouseId"
                reference="warehouses"
                link="show"
            >
                <TextField source="name" />
            </ReferenceField>
        );
    }
    return null;
};

const BoxFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={Object.keys(BoxStatus).map(c => ({ id: c, name: c }))}
        />

        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user => (user.name ? user.name : user.email)}
            />
        </ReferenceInput>

        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput />
        </ReferenceInput>
    </Filter>
);

const defaultColumns: ColumnConfig[] = [
    { id: 'serial', label: 'Serial', visible: true, order: 0 },
    { id: 'id', label: 'Box ID', visible: true, order: 1 },
    { id: 'code', label: 'Box Code', visible: true, order: 2 },
    { id: 'status', label: 'Status', visible: true, order: 3 },
    { id: 'customer', label: 'Customer', visible: true, order: 4 },
    { id: 'partner', label: 'Partner', visible: true, order: 5 },
    { id: 'country', label: 'Country', visible: true, order: 6 },
    { id: 'warehouse', label: 'Warehouse', visible: true, order: 7 },
    { id: 'show', label: 'Show', visible: true, order: 8 },

    // Optional columns (hidden by default)
    { id: 'createdAt', label: 'Created At', visible: false, order: 9 },
    { id: 'updatedAt', label: 'Updated At', visible: false, order: 10 },
    { id: 'userId', label: 'User ID', visible: false, order: 11 },
    { id: 'partnerId', label: 'Partner ID', visible: false, order: 12 },
    { id: 'countryId', label: 'Country ID', visible: false, order: 13 },
    { id: 'warehouseId', label: 'Warehouse ID', visible: false, order: 14 },

    // User details from nested object
    { id: 'userEmail', label: 'Customer Email', visible: false, order: 15 },
    { id: 'userPhone', label: 'Customer Phone', visible: false, order: 16 },
    { id: 'userPhone2', label: 'Customer Phone 2', visible: false, order: 17 },
    { id: 'userType', label: 'Customer Type', visible: false, order: 18 },
    { id: 'userStatus', label: 'Customer Status', visible: false, order: 19 },
    {
        id: 'userCurrency',
        label: 'Customer Currency',
        visible: false,
        order: 20,
    },
    { id: 'userCredits', label: 'Customer Credits', visible: false, order: 21 },
    {
        id: 'userRevision',
        label: 'Customer Revision',
        visible: false,
        order: 22,
    },
    { id: 'userTrn', label: 'Customer TRN', visible: false, order: 23 },
    {
        id: 'userDeposits',
        label: 'Customer Deposits',
        visible: false,
        order: 24,
    },
    {
        id: 'userStorageRate',
        label: 'Customer Storage Rate',
        visible: false,
        order: 25,
    },
    {
        id: 'userEnglishName',
        label: 'Customer English Name',
        visible: false,
        order: 26,
    },
    {
        id: 'userArabicName',
        label: 'Customer Arabic Name',
        visible: false,
        order: 27,
    },
    { id: 'userGender', label: 'Customer Gender', visible: false, order: 28 },
    {
        id: 'userCurrentStatus',
        label: 'Customer Current Status',
        visible: false,
        order: 29,
    },
    {
        id: 'userLanguage',
        label: 'Customer Language',
        visible: false,
        order: 30,
    },

    // Partner details from nested object
    {
        id: 'partnerCountryId',
        label: 'Partner Country ID',
        visible: false,
        order: 31,
    },
    {
        id: 'partnerUserId',
        label: 'Partner User ID',
        visible: false,
        order: 32,
    },
    {
        id: 'partnerCreatedAt',
        label: 'Partner Created At',
        visible: false,
        order: 33,
    },
    {
        id: 'partnerUpdatedAt',
        label: 'Partner Updated At',
        visible: false,
        order: 34,
    },

    // Country details from nested object
    { id: 'countryCode', label: 'Country Code', visible: false, order: 35 },
    {
        id: 'countryCurrency',
        label: 'Country Currency',
        visible: false,
        order: 36,
    },
    {
        id: 'countryDecimalPlaces',
        label: 'Country Decimal Places',
        visible: false,
        order: 37,
    },
    {
        id: 'countrySalesContactName',
        label: 'Sales Contact Name',
        visible: false,
        order: 38,
    },
    {
        id: 'countrySalesContactPhone',
        label: 'Sales Contact Phone',
        visible: false,
        order: 39,
    },
    {
        id: 'countrySalesContactEmail',
        label: 'Sales Contact Email',
        visible: false,
        order: 40,
    },
    {
        id: 'countryLatePaymentFee',
        label: 'Late Payment Fee',
        visible: false,
        order: 41,
    },
    { id: 'countryVat', label: 'VAT', visible: false, order: 42 },
    {
        id: 'countryLegalEntity',
        label: 'Legal Entity',
        visible: false,
        order: 43,
    },
    {
        id: 'countryAddress',
        label: 'Country Address',
        visible: false,
        order: 44,
    },
    { id: 'countryTrn', label: 'Country TRN', visible: false, order: 45 },

    // Warehouse details from nested object
    { id: 'warehouseCode', label: 'Warehouse Code', visible: false, order: 46 },
    {
        id: 'warehouseSizeSqm',
        label: 'Warehouse Size (sqm)',
        visible: false,
        order: 47,
    },
    {
        id: 'warehouseCityId',
        label: 'Warehouse City ID',
        visible: false,
        order: 48,
    },
    {
        id: 'warehouseInLocationId',
        label: 'Warehouse In Location ID',
        visible: false,
        order: 49,
    },
    {
        id: 'warehouseOutLocationId',
        label: 'Warehouse Out Location ID',
        visible: false,
        order: 50,
    },
    {
        id: 'warehouseActive',
        label: 'Warehouse Active',
        visible: false,
        order: 51,
    },
    {
        id: 'warehouseCreatedAt',
        label: 'Warehouse Created At',
        visible: false,
        order: 52,
    },
    {
        id: 'warehouseUpdatedAt',
        label: 'Warehouse Updated At',
        visible: false,
        order: 53,
    },

    { id: 'edit', label: 'Edit', visible: false, order: 54 },
];

const BoxList = props => {
    const renderColumn = (columnId: string) => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" label="Box ID" />;
            case 'code':
                return (
                    <TextField key={columnId} source="code" label="Box Code" />
                );
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="box"
                    />
                );
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => <CustomerNameField record={record} />}
                    />
                );
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerNameField record={record} />}
                    />
                );
            case 'country':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country"
                        render={record => <CountryNameField record={record} />}
                    />
                );
            case 'warehouse':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse"
                        render={record => (
                            <WarehouseNameField record={record} />
                        )}
                    />
                );
            case 'show':
                return <ShowButton key={columnId} style={{ padding: 0 }} />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        source="createdAt"
                        label="Created At"
                        locales="en-GB"
                    />
                );
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        source="updatedAt"
                        label="Updated At"
                        locales="en-GB"
                        showTime
                    />
                );
            case 'userId':
                return (
                    <TextField key={columnId} source="userId" label="User ID" />
                );
            case 'partnerId':
                return (
                    <TextField
                        key={columnId}
                        source="partnerId"
                        label="Partner ID"
                    />
                );
            case 'countryId':
                return (
                    <TextField
                        key={columnId}
                        source="countryId"
                        label="Country ID"
                    />
                );
            case 'warehouseId':
                return (
                    <TextField
                        key={columnId}
                        source="warehouseId"
                        label="Warehouse ID"
                    />
                );
            case 'userEmail':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Email"
                        render={record => record.user?.email || '-'}
                    />
                );
            case 'userPhone':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Phone"
                        render={record => record.user?.phone || '-'}
                    />
                );
            case 'userPhone2':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Phone 2"
                        render={record => record.user?.phone2 || '-'}
                    />
                );
            case 'userType':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Type"
                        render={record => (
                            <Chip
                                label={record.user?.type || 'N/A'}
                                color={
                                    record.user?.type === 'b2c'
                                        ? 'primary'
                                        : 'secondary'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'userStatus':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Status"
                        render={record => (
                            <Chip
                                label={record.user?.status || 'N/A'}
                                color={
                                    record.user?.status === 'active'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'userCurrency':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Currency"
                        render={record => record.user?.currency || '-'}
                    />
                );
            case 'userCredits':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Credits"
                        render={record => {
                            if (!record.user?.credits) return '-';
                            const currency = record.user?.currency || 'USD';
                            const credits = record.user.credits / 1000;
                            return `${currency} ${credits.toLocaleString()}`;
                        }}
                    />
                );
            case 'userRevision':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Revision"
                        render={record => record.user?.revision || '-'}
                    />
                );
            case 'userTrn':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer TRN"
                        render={record => record.user?.trn || '-'}
                    />
                );
            case 'userDeposits':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Deposits"
                        render={record => {
                            if (!record.user?.deposits) return '-';
                            const currency = record.user?.currency || 'USD';
                            const deposits = record.user.deposits / 1000;
                            return `${currency} ${deposits.toLocaleString()}`;
                        }}
                    />
                );
            case 'userStorageRate':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Storage Rate"
                        render={record => record.user?.storageRate || '-'}
                    />
                );
            case 'userEnglishName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer English Name"
                        render={record => record.user?.englishName || '-'}
                    />
                );
            case 'userArabicName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Arabic Name"
                        render={record => record.user?.arabicName || '-'}
                    />
                );
            case 'userGender':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Gender"
                        render={record => (
                            <Chip
                                label={record.user?.gender || 'N/A'}
                                color={
                                    record.user?.gender === 'male'
                                        ? 'primary'
                                        : record.user?.gender === 'female'
                                        ? 'secondary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'userCurrentStatus':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Current Status"
                        render={record => (
                            <Chip
                                label={record.user?.currentStatus || 'N/A'}
                                color={
                                    record.user?.currentStatus ===
                                    'EXISTING_CUSTOMER'
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'userLanguage':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Language"
                        render={record => record.user?.language || '-'}
                    />
                );
            case 'partnerCountryId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner Country ID"
                        render={record => record.partner?.countryId || '-'}
                    />
                );
            case 'partnerUserId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner User ID"
                        render={record => record.partner?.userId || '-'}
                    />
                );
            case 'partnerCreatedAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner Created At"
                        render={record =>
                            record.partner?.createdAt
                                ? new Date(
                                      record.partner.createdAt
                                  ).toLocaleDateString('en-GB')
                                : '-'
                        }
                    />
                );
            case 'partnerUpdatedAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner Updated At"
                        render={record =>
                            record.partner?.updatedAt
                                ? new Date(
                                      record.partner.updatedAt
                                  ).toLocaleDateString('en-GB')
                                : '-'
                        }
                    />
                );
            case 'countryCode':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country Code"
                        render={record => record.country?.code || '-'}
                    />
                );
            case 'countryCurrency':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country Currency"
                        render={record => record.country?.currency || '-'}
                    />
                );
            case 'countryDecimalPlaces':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country Decimal Places"
                        render={record => record.country?.decimalPlaces || '-'}
                    />
                );
            case 'countrySalesContactName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Sales Contact Name"
                        render={record =>
                            record.country?.salesContactName || '-'
                        }
                    />
                );
            case 'countrySalesContactPhone':
                return (
                    <FunctionField
                        key={columnId}
                        label="Sales Contact Phone"
                        render={record =>
                            record.country?.salesContactPhone || '-'
                        }
                    />
                );
            case 'countrySalesContactEmail':
                return (
                    <FunctionField
                        key={columnId}
                        label="Sales Contact Email"
                        render={record =>
                            record.country?.salesContactEmail || '-'
                        }
                    />
                );
            case 'countryLatePaymentFee':
                return (
                    <FunctionField
                        key={columnId}
                        label="Late Payment Fee"
                        render={record => record.country?.latePaymentFee || '-'}
                    />
                );
            case 'countryVat':
                return (
                    <FunctionField
                        key={columnId}
                        label="VAT"
                        render={record => record.country?.vat || '-'}
                    />
                );
            case 'countryLegalEntity':
                return (
                    <FunctionField
                        key={columnId}
                        label="Legal Entity"
                        render={record => (
                            <div
                                style={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {record.country?.legalEntity || '-'}
                            </div>
                        )}
                    />
                );
            case 'countryAddress':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country Address"
                        render={record => (
                            <div
                                style={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {record.country?.address || '-'}
                            </div>
                        )}
                    />
                );
            case 'countryTrn':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country TRN"
                        render={record => record.country?.trn || '-'}
                    />
                );
            case 'warehouseCode':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Code"
                        render={record => record.warehouse?.code || '-'}
                    />
                );
            case 'warehouseSizeSqm':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Size (sqm)"
                        render={record => record.warehouse?.size_sqm || '-'}
                    />
                );
            case 'warehouseCityId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse City ID"
                        render={record => record.warehouse?.cityId || '-'}
                    />
                );
            case 'warehouseInLocationId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse In Location ID"
                        render={record => record.warehouse?.inLocationId || '-'}
                    />
                );
            case 'warehouseOutLocationId':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Out Location ID"
                        render={record =>
                            record.warehouse?.outLocationId || '-'
                        }
                    />
                );
            case 'warehouseActive':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Active"
                        render={record => (
                            <Chip
                                label={record.warehouse?.active ? 'Yes' : 'No'}
                                color={
                                    record.warehouse?.active
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'warehouseCreatedAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Created At"
                        render={record =>
                            record.warehouse?.createdAt
                                ? new Date(
                                      record.warehouse.createdAt
                                  ).toLocaleDateString('en-GB')
                                : '-'
                        }
                    />
                );
            case 'warehouseUpdatedAt':
                return (
                    <FunctionField
                        key={columnId}
                        label="Warehouse Updated At"
                        render={record =>
                            record.warehouse?.updatedAt
                                ? new Date(
                                      record.warehouse.updatedAt
                                  ).toLocaleDateString('en-GB')
                                : '-'
                        }
                    />
                );
            case 'edit':
                return (
                    <EditButton
                        key={columnId}
                        color="secondary"
                        style={{ padding: 0 }}
                    />
                );
            default:
                return null;
        }
    };

    // Ensure we have all required props
    const combinedProps = {
        title: 'Boxes',
        defaultColumns,
        storageKey: 'boxListColumns',
        renderColumn,
        filtersComponent: <BoxFilter />,
        sort: { field: 'id', order: 'DESC' },
        ...props,
    };

    return <CustomizableGrid {...combinedProps} />;
};

BoxList.defaultProps = {
    resource: 'boxes',
};

export default BoxList;
